import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowCounterClockwise, TrashSimple, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { API } from '@/config/api';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function TrashPanel({ recruiters, students, loading, onReload, onAfterMutation }) {
  const [selRec, setSelRec] = useState([]);
  const [selStu, setSelStu] = useState([]);
  const [subTab, setSubTab] = useState('recruiters');
  const [busy, setBusy] = useState(false);

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);

  const restoreRecruiter = async (id) => {
    if (!window.confirm('Restore this recruiter? Their user account will be re-enabled.')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trash/recruiters/${id}/restore`);
      toast.success('Recruiter restored');
      await onReload();
      await onAfterMutation();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore');
    } finally { setBusy(false); }
  };

  const purgeRecruiter = async (id) => {
    if (!window.confirm('PERMANENTLY delete this recruiter? This CANNOT be undone.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/trash/recruiters/${id}`);
      toast.success('Recruiter permanently deleted');
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to purge');
    } finally { setBusy(false); }
  };

  const restoreStudent = async (id) => {
    if (!window.confirm('Restore this student?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/trash/students/${id}/restore`);
      toast.success('Student restored');
      await onReload();
      await onAfterMutation();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to restore');
    } finally { setBusy(false); }
  };

  const purgeStudent = async (id) => {
    if (!window.confirm('PERMANENTLY delete this student and all their files? This CANNOT be undone.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/trash/students/${id}`);
      toast.success('Student permanently deleted');
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to purge');
    } finally { setBusy(false); }
  };

  const bulkRestore = async () => {
    const ids = subTab === 'recruiters' ? selRec : selStu;
    if (!ids.length) return;
    if (!window.confirm(`Restore ${ids.length} ${subTab === 'recruiters' ? 'recruiter' : 'student'}(s)?`)) return;
    setBusy(true);
    try {
      const path = subTab === 'recruiters' ? 'recruiters' : 'students';
      await axios.post(`${API}/admin/trash/${path}/bulk-restore`, { ids });
      toast.success('Restored');
      subTab === 'recruiters' ? setSelRec([]) : setSelStu([]);
      await onReload();
      await onAfterMutation();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to bulk restore');
    } finally { setBusy(false); }
  };

  const bulkPurge = async () => {
    const ids = subTab === 'recruiters' ? selRec : selStu;
    if (!ids.length) return;
    if (!window.confirm(`PERMANENTLY delete ${ids.length} ${subTab === 'recruiters' ? 'recruiter' : 'student'}(s)? This CANNOT be undone.`)) return;
    setBusy(true);
    try {
      const path = subTab === 'recruiters' ? 'recruiters' : 'students';
      await axios.post(`${API}/admin/trash/${path}/bulk-purge`, { ids });
      toast.success('Permanently deleted');
      subTab === 'recruiters' ? setSelRec([]) : setSelStu([]);
      await onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to bulk purge');
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-trash-panel">
      <div className="mb-4 px-4 py-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2" data-testid="trash-notice">
        <Warning size={18} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900">
          <p className="font-medium">Deleted recruiters and students land here.</p>
          <p className="text-[13px] text-amber-800 mt-0.5">You can <b>Restore</b> them to their live tab, or <b>Delete Permanently</b> to purge forever. Items deleted before this Trash feature was introduced were hard-deleted and cannot be recovered.</p>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-4 border-b border-gray-200">
        {[{ id: 'recruiters', label: `Recruiters (${recruiters.length})` }, { id: 'students', label: `Students (${students.length})` }].map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${subTab === t.id ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            data-testid={`trash-subtab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(subTab === 'recruiters' ? selRec : selStu).length > 0 && (
        <div className="mb-3 flex items-center gap-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md" data-testid="trash-bulk-bar">
          <span className="text-sm text-blue-900">{(subTab === 'recruiters' ? selRec : selStu).length} selected</span>
          <Button size="sm" variant="outline" onClick={bulkRestore} disabled={busy} data-testid="trash-bulk-restore">
            <ArrowCounterClockwise size={14} className="mr-1" /> Restore selected
          </Button>
          <Button size="sm" variant="destructive" onClick={bulkPurge} disabled={busy} data-testid="trash-bulk-purge">
            <TrashSimple size={14} className="mr-1" /> Delete permanently
          </Button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-400 py-8 text-center">Loading Trash…</p>
      ) : subTab === 'recruiters' ? (
        recruiters.length === 0 ? (
          <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-md" data-testid="trash-recruiters-empty">
            <TrashSimple size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Trash is empty. Deleted recruiters will appear here.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <table className="min-w-full text-sm" data-testid="trash-recruiters-table">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={selRec.length === recruiters.length && recruiters.length > 0}
                      onCheckedChange={() => setSelRec(selRec.length === recruiters.length ? [] : recruiters.map(r => r.id))}
                      data-testid="trash-select-all-recruiters"
                    />
                  </th>
                  <th className="px-3 py-3 text-left font-medium">Name</th>
                  <th className="px-3 py-3 text-left font-medium">Email</th>
                  <th className="px-3 py-3 text-left font-medium">Company</th>
                  <th className="px-3 py-3 text-left font-medium">Deleted</th>
                  <th className="px-3 py-3 text-left font-medium">By</th>
                  <th className="px-3 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recruiters.map(r => (
                  <tr key={r.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50" data-testid={`trash-recruiter-row-${r.id}`}>
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selRec.includes(r.id)}
                        onCheckedChange={() => toggle(selRec, setSelRec, r.id)}
                      />
                    </td>
                    <td className="px-3 py-3 text-gray-900 font-medium">{r.full_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-700">{r.email}</td>
                    <td className="px-3 py-3 text-gray-700">{r.company_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{fmt(r.deleted_at)}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{r.deleted_by || '—'}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => restoreRecruiter(r.id)} disabled={busy} data-testid={`trash-restore-recruiter-${r.id}`}>
                          <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => purgeRecruiter(r.id)} disabled={busy} data-testid={`trash-purge-recruiter-${r.id}`}>
                          <TrashSimple size={14} className="mr-1" /> Delete permanently
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        students.length === 0 ? (
          <div className="text-center py-16 text-gray-500 border border-dashed border-gray-200 rounded-md" data-testid="trash-students-empty">
            <TrashSimple size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">Trash is empty. Deleted students will appear here.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <table className="min-w-full text-sm" data-testid="trash-students-table">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={selStu.length === students.length && students.length > 0}
                      onCheckedChange={() => setSelStu(selStu.length === students.length ? [] : students.map(s => s.id))}
                      data-testid="trash-select-all-students"
                    />
                  </th>
                  <th className="px-3 py-3 text-left font-medium">Name</th>
                  <th className="px-3 py-3 text-left font-medium">Email</th>
                  <th className="px-3 py-3 text-left font-medium">Recruiter</th>
                  <th className="px-3 py-3 text-left font-medium">College</th>
                  <th className="px-3 py-3 text-left font-medium">Deleted</th>
                  <th className="px-3 py-3 text-left font-medium">By</th>
                  <th className="px-3 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {students.map(s => (
                  <tr key={s.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50" data-testid={`trash-student-row-${s.id}`}>
                    <td className="px-3 py-3">
                      <Checkbox
                        checked={selStu.includes(s.id)}
                        onCheckedChange={() => toggle(selStu, setSelStu, s.id)}
                      />
                    </td>
                    <td className="px-3 py-3 text-gray-900 font-medium">{s.full_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-700">{s.email || '—'}</td>
                    <td className="px-3 py-3 text-gray-700">{s.recruiter_name || s.recruiter_email || '—'}</td>
                    <td className="px-3 py-3 text-gray-700">{s.assigned_college_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{fmt(s.deleted_at)}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{s.deleted_by || '—'}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => restoreStudent(s.id)} disabled={busy} data-testid={`trash-restore-student-${s.id}`}>
                          <ArrowCounterClockwise size={14} className="mr-1" /> Restore
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => purgeStudent(s.id)} disabled={busy} data-testid={`trash-purge-student-${s.id}`}>
                          <TrashSimple size={14} className="mr-1" /> Delete permanently
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
